import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import taskService from '../../../services/taskService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import priorityMasterService from '../../../services/priorityMasterService'
import autoIncrementService from '../../../services/autoIncrementService'
import { 
  FileText,
  Calendar,
  User,
  AlertCircle,
  RotateCcw,
  HelpCircle
} from 'lucide-react'

const TaskForm = ({ mode = 'create', taskId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [taskDataLoading, setTaskDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingTaskNumber, setIsLoadingTaskNumber] = useState(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: [],
    priorities: [],
    tasks: [] // For dependencies and parent task
  })

  const [formData, setFormData] = useState({
    taskNumber: '',
    taskName: '',
    taskDescription: '',
    project: '',
    assignedTo: '',
    taskStatus: 'Not Started',
    priority: '',
    startDate: '',
    dueDate: '',
    completedDate: '',
    estimatedHours: 0,
    actualHours: 0,
    dependencies: [],
    parentTask: '',
    tags: []
  })

  const [errors, setErrors] = useState({})
  const [newTag, setNewTag] = useState('')

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next task number (only for new tasks)
  useEffect(() => {
    if (!isEditMode && !isViewMode && !isLoadingTaskNumber) {
      fetchNextTaskNumber()
    }
  }, [isEditMode, isViewMode])

  // Load task data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && taskId && masterDataLoaded) {
      if (initialData) {
        console.log('Using initialData:', initialData)
        populateFormData(initialData)
      } else if (!taskDataLoading) {
        console.log('Loading task data for ID:', taskId)
        loadTaskData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, isEditMode, isViewMode, masterDataLoaded, taskDataLoading])

  const loadTaskData = async () => {
    try {
      setTaskDataLoading(true)
      const response = await taskService.getTaskById(taskId)
      
      if (response.success && response.data) {
        console.log('Task data loaded:', response.data)
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load task')
        navigate('/projects/tasks/list')
      }
    } catch (error) {
      console.error('Error loading task:', error)
      toast.error('Failed to load task')
      navigate('/projects/tasks/list')
    } finally {
      setTaskDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    console.log('Populating form data:', data)
    console.log('Priority from API:', data.priority)
    console.log('Priority _id:', data.priority?._id)
    console.log('Priority id:', data.priority?.id)
    console.log('Master Data Priorities loaded:', masterData.priorities.length > 0)
    
    // Extract priority ID - handle both populated object and direct ID
    let priorityValue = ''
    if (data.priority) {
      if (typeof data.priority === 'object' && data.priority !== null) {
        // Try _id first (Mongoose default), then id (JSON serialized)
        if (data.priority._id !== undefined && data.priority._id !== null) {
          priorityValue = String(data.priority._id)
        } else if (data.priority.id !== undefined && data.priority.id !== null) {
          priorityValue = String(data.priority.id)
        } else {
          console.warn('Priority object has no _id or id field:', data.priority)
          priorityValue = ''
        }
      } else {
        // Direct ID value (string or number)
        priorityValue = String(data.priority)
      }
    }
    console.log('Extracted priority value:', priorityValue)
    console.log('Available priority option IDs:', masterData.priorities.map(p => String(p._id || p.id)))
    
    setFormData({
      taskNumber: data.taskNumber || '',
      taskName: data.taskName || '',
      taskDescription: data.taskDescription || '',
      project: data.project?._id || data.project || '',
      assignedTo: data.assignedTo?._id || data.assignedTo || '',
      taskStatus: data.taskStatus || 'Not Started',
      priority: priorityValue, // Use the extracted value
      startDate: data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '',
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString().split('T')[0] : '',
      completedDate: data.completedDate ? new Date(data.completedDate).toISOString().split('T')[0] : '',
      estimatedHours: data.estimatedHours || 0,
      actualHours: data.actualHours || 0,
      dependencies: (data.dependencies || []).map(dep => dep._id || dep),
      parentTask: data.parentTask?._id || data.parentTask || '',
      tags: data.tags || []
    })
    setErrors({})
  }

  const fetchNextTaskNumber = async () => {
    try {
      setIsLoadingTaskNumber(true)
      const response = await autoIncrementService.getCurrentValue('TASK')
      
      if (response.success && response.data) {
        const sequence = response.data
        // Show current value only - backend will increment on save
        // Use formattedValue directly from backend which has correct prefix and digits from config
        if (sequence.formattedValue) {
          setFormData(prev => ({
            ...prev,
            taskNumber: sequence.formattedValue
          }))
        } else {
          // Fallback if formattedValue not available
          setFormData(prev => ({
            ...prev,
            taskNumber: 'TASK-000001'
          }))
        }
      } else {
        // If sequence doesn't exist, show placeholder
        // Backend will create sequence on first save with default format
        setFormData(prev => ({
          ...prev,
          taskNumber: 'TASK-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next task number:', error)
      // If sequence doesn't exist yet, show placeholder
      // Backend will create sequence on first save
      setFormData(prev => ({
        ...prev,
        taskNumber: 'TASK-000001'
      }))
    } finally {
      setIsLoadingTaskNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes,
        prioritiesRes,
        tasksRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        priorityMasterService.getAllPriorityMasters({ limit: 1000 }),
        taskService.getAllTasks({ limit: 1000 })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
          if (res.projects) {
            return res.projects
          }
          if (res.tasks) {
            return res.tasks
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      const getPrioritiesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          // Backend returns { success: true, data: [...] }
          if (res.success !== false && res.data) {
            // data is an array of priority masters
            if (Array.isArray(res.data)) {
              return res.data
            }
            return res.data
          }
          // Fallback: if response is directly an array
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes),
        priorities: getPrioritiesData(prioritiesRes),
        tasks: getData(tasksRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Priorities', result: prioritiesRes },
        { name: 'Tasks', result: tasksRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        // Check if errors were already handled by interceptor
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 4) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      } else {
        console.log('All master data loaded successfully')
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleDependencyChange = (taskId, checked) => {
    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          dependencies: [...prev.dependencies, taskId]
        }
      } else {
        return {
          ...prev,
          dependencies: prev.dependencies.filter(id => id !== taskId)
        }
      }
    })
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.taskName.trim()) {
      newErrors.taskName = 'Task name is required'
    }

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }

    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Assignee is required'
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required'
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required'
    }

    if (formData.startDate && formData.dueDate) {
      const start = new Date(formData.startDate)
      const due = new Date(formData.dueDate)
      if (due < start) {
        newErrors.dueDate = 'Due date must be after start date'
      }
    }

    if (formData.estimatedHours < 0) {
      newErrors.estimatedHours = 'Estimated hours cannot be negative'
    }

    if (formData.actualHours < 0) {
      newErrors.actualHours = 'Actual hours cannot be negative'
    }

    setErrors(newErrors)
    
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0]
      const errorElement = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus()
      }
    }
    
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      const errorCount = Object.keys(errors).length
      const errorFields = Object.keys(errors).map(field => {
        const fieldNames = {
          taskName: 'Task Name',
          project: 'Project',
          assignedTo: 'Assignee',
          startDate: 'Start Date',
          dueDate: 'Due Date',
          estimatedHours: 'Estimated Hours',
          actualHours: 'Actual Hours'
        }
        return fieldNames[field] || field
      }).join(', ')
      toast.error(`Please fix ${errorCount} error(s): ${errorFields}`)
      return
    }

    try {
      setLoading(true)
      let response
      if (isEditMode) {
        response = await taskService.updateTask(taskId, formData)
        if (response.success) {
          toast.success('Task updated successfully')
          navigate('/projects/tasks/list')
        } else {
          toast.error(response.message || 'Failed to update task')
        }
      } else {
        // Remove taskNumber when creating - backend will auto-generate it via pre-save hook
        // The preview number is just for display, not for saving
        const { taskNumber, ...createData } = formData
        response = await taskService.createTask(createData)
        if (response.success) {
          toast.success('Task created successfully')
          navigate('/projects/tasks/list')
        } else {
          toast.error(response.message || 'Failed to create task')
        }
      }
    } catch (error) {
      console.error('Error saving task:', error)
      toast.error(error.response?.data?.message || 'Failed to save task')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      taskNumber: '',
      taskName: '',
      taskDescription: '',
      project: '',
      assignedTo: '',
      taskStatus: 'Not Started',
      priority: '',
      startDate: '',
      dueDate: '',
      completedDate: '',
      estimatedHours: 0,
      actualHours: 0,
      dependencies: [],
      parentTask: '',
      tags: []
    })
    setErrors({})
    setNewTag('')
  }

  const handleTestData = () => {
    const today = new Date()
    const startDate = new Date(today)
    startDate.setDate(today.getDate() + 1)
    const dueDate = new Date(today)
    dueDate.setDate(today.getDate() + 7)

    setFormData(prev => ({
      ...prev,
      // Preserve taskNumber - don't reset it
      taskName: 'Implement User Authentication Module',
      taskDescription: 'Develop secure user authentication system with JWT tokens, password hashing, and role-based access control',
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      assignedTo: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      taskStatus: 'Not Started',
      priority: masterData.priorities.length > 0 ? (masterData.priorities[0]._id || masterData.priorities[0].id) : '',
      startDate: startDate.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      completedDate: '',
      estimatedHours: 40,
      actualHours: 0,
      dependencies: [],
      parentTask: '',
      tags: ['Authentication', 'Security', 'Backend', 'High Priority']
    }))
    setErrors({})
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Task List', path: '/projects/tasks/list', onClick: () => navigate('/projects/tasks/list') },
    { label: isEditMode ? 'Edit Task' : isViewMode ? 'View Task' : 'Create New', path: isEditMode ? `/projects/tasks/edit/${taskId}` : isViewMode ? `/projects/tasks/view/${taskId}` : '/projects/tasks/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'details', label: 'Details & Timeline', icon: Calendar },
    { id: 'dependencies', label: 'Dependencies', icon: User }
  ]

  const helpContent = (
    <>
      <h3>What is Task Entry?</h3>
      <p>Task Entry lets you <strong>create and manage project tasks</strong>—tasks, subtasks, and dependencies. You define task name, project, assignee, status, priority, dates, estimated/actual hours, and optional parent task.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Task number, name, description, project, assigned to, status, priority.</p>
      <p><strong>Details &amp; Timeline</strong> – Start date, due date, completed date, estimated hours, actual hours.</p>
      <p><strong>Dependencies</strong> – Parent task (for subtasks) and dependency tasks.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong> and fill task name and required fields. Use <strong>Create Task</strong> to save. Tasks can be viewed in list or Kanban in Task Management.</p>
    </>
  )

  if (taskDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading task data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Task Entry</h1>
          <p>Create and manage task information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Task Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                disabled={isReadOnly}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    taskName: 'Task Name',
                    project: 'Project',
                    assignedTo: 'Assignee',
                    startDate: 'Start Date',
                    dueDate: 'Due Date',
                    estimatedHours: 'Estimated Hours',
                    actualHours: 'Actual Hours'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>
                
                {/* Row 1: Task Number, Task Name, Project */}
                <div className="grid-row-3">
                  {/* Task Number */}
                  <div className="form-group">
                    <label htmlFor="taskNumber" className="form-label">
                      Task Number
                    </label>
                    <input
                      id="taskNumber"
                      type="text"
                      value={isLoadingTaskNumber ? 'Loading...' : formData.taskNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Task Name */}
                  <div className="form-group">
                    <label htmlFor="taskName" className="form-label required">
                      Task Name
                    </label>
                    <input
                      id="taskName"
                      type="text"
                      value={formData.taskName}
                      onChange={(e) => handleInputChange('taskName', e.target.value)}
                      className="form-input"
                      placeholder="Enter task name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.taskName && (
                      <p className="form-error">{errors.taskName}</p>
                    )}
                  </div>

                  {/* Project */}
                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.map((project) => {
                        const projectId = project._id || project.id
                        return (
                          <option key={projectId} value={String(projectId)}>
                            {project.projectNumber} - {project.projectName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Task Description */}
                <div className="form-group">
                  <label htmlFor="taskDescription" className="form-label">
                    Task Description
                  </label>
                  <input
                    id="taskDescription"
                    type="text"
                    value={formData.taskDescription}
                    onChange={(e) => handleInputChange('taskDescription', e.target.value)}
                    className="form-input"
                    placeholder="Enter task description"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Row 3: Assigned To, Status, Priority */}
                <div className="grid-row-3">
                  {/* Assigned To */}
                  <div className="form-group">
                    <label htmlFor="assignedTo" className="form-label required">
                      Assigned To
                    </label>
                    <select
                      id="assignedTo"
                      value={formData.assignedTo}
                      onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Assignee</option>
                      {masterData.employees.map((employee) => {
                        const employeeId = employee._id || employee.id
                        return (
                          <option key={employeeId} value={String(employeeId)}>
                            {employee.employeeName} {employee.designation ? `- ${employee.designation}` : ''}
                          </option>
                        )
                      })}
                    </select>
                    {errors.assignedTo && (
                      <p className="form-error">{errors.assignedTo}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="taskStatus" className="form-label required">
                      Status
                    </label>
                    <select
                      id="taskStatus"
                      value={formData.taskStatus}
                      onChange={(e) => handleInputChange('taskStatus', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div className="form-group">
                    <label htmlFor="priority" className="form-label">
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Priority</option>
                      {masterData.priorities && masterData.priorities.length > 0 ? (
                        masterData.priorities.map((priority) => {
                          const priorityId = priority._id || priority.id
                          return (
                            <option key={priorityId} value={String(priorityId)}>
                              {priority.priorityName || priority.name || 'Unknown'}
                            </option>
                          )
                        })
                      ) : (
                        <option value="" disabled>No priorities available</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Tags Section */}
                <div className="form-group">
                  <label className="form-label">Tags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          backgroundColor: '#EFF6FF',
                          color: '#1E40AF',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#1E40AF',
                              cursor: 'pointer',
                              padding: 0,
                              marginLeft: '4px',
                              fontSize: '14px',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {!isReadOnly && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addTag()
                          }
                        }}
                        className="form-input"
                        placeholder="Add tag and press Enter"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={addTag}
                      >
                        Add Tag
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div>
                <div className="section-header">
                  <h3>Details & Timeline</h3>
                </div>
                
                {/* Row 1: Start Date, Due Date, Completed Date */}
                <div className="grid-row-3">
                  {/* Start Date */}
                  <div className="form-group">
                    <label htmlFor="startDate" className="form-label required">
                      Start Date
                    </label>
                    <input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.startDate && (
                      <p className="form-error">{errors.startDate}</p>
                    )}
                  </div>

                  {/* Due Date */}
                  <div className="form-group">
                    <label htmlFor="dueDate" className="form-label required">
                      Due Date
                    </label>
                    <input
                      id="dueDate"
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.dueDate && (
                      <p className="form-error">{errors.dueDate}</p>
                    )}
                  </div>

                  {/* Completed Date */}
                  <div className="form-group">
                    <label htmlFor="completedDate" className="form-label">
                      Completed Date
                    </label>
                    <input
                      id="completedDate"
                      type="date"
                      value={formData.completedDate}
                      onChange={(e) => handleInputChange('completedDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Row 2: Estimated Hours, Actual Hours */}
                <div className="grid-row-3">
                  {/* Estimated Hours */}
                  <div className="form-group">
                    <label htmlFor="estimatedHours" className="form-label">
                      Estimated Hours
                    </label>
                    <input
                      id="estimatedHours"
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.estimatedHours}
                      onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.estimatedHours && (
                      <p className="form-error">{errors.estimatedHours}</p>
                    )}
                  </div>

                  {/* Actual Hours */}
                  <div className="form-group">
                    <label htmlFor="actualHours" className="form-label">
                      Actual Hours
                    </label>
                    <input
                      id="actualHours"
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.actualHours}
                      onChange={(e) => handleInputChange('actualHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.actualHours && (
                      <p className="form-error">{errors.actualHours}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'dependencies' && (
              <div>
                <div className="section-header">
                  <h3>Dependencies</h3>
                </div>
                
                {/* Parent Task */}
                <div className="form-group">
                  <label htmlFor="parentTask" className="form-label">
                    Parent Task
                  </label>
                  <select
                    id="parentTask"
                    value={formData.parentTask}
                    onChange={(e) => handleInputChange('parentTask', e.target.value)}
                    className="form-input"
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  >
                    <option value="">Select Parent Task (Optional)</option>
                    {masterData.tasks
                      .filter(task => !taskId || task._id !== taskId || task.id !== taskId)
                      .map((task) => {
                        const taskIdValue = task._id || task.id
                        return (
                          <option key={taskIdValue} value={String(taskIdValue)}>
                            {task.taskNumber} - {task.taskName}
                          </option>
                        )
                      })}
                  </select>
                </div>

                {/* Dependencies */}
                <div className="form-group">
                  <label className="form-label">Task Dependencies</label>
                  <div style={{ 
                    border: '1px solid #D1D5DB', 
                    borderRadius: '4px', 
                    padding: '16px', 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    backgroundColor: isReadOnly ? '#f3f4f6' : '#fff'
                  }}>
                    {masterData.tasks.length === 0 ? (
                      <p style={{ color: '#6B7280', margin: 0 }}>No tasks available</p>
                    ) : (
                      masterData.tasks
                        .filter(task => !taskId || task._id !== taskId || task.id !== taskId)
                        .map((task) => {
                          const taskIdValue = task._id || task.id
                          const isChecked = formData.dependencies.includes(String(taskIdValue))
                          return (
                            <label
                              key={taskIdValue}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px',
                                marginBottom: '8px',
                                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                opacity: isReadOnly ? 0.6 : 1
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => handleDependencyChange(String(taskIdValue), e.target.checked)}
                                disabled={isReadOnly}
                                style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
                              />
                              <span>
                                <strong>{task.taskNumber}</strong> - {task.taskName}
                              </span>
                            </label>
                          )
                        })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button type="button" className="btn-secondary" onClick={handleReset}>
                  Reset
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? (
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  ) : (
                    <span>{isEditMode ? 'Update Task' : 'Create Task'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => navigate('/projects/tasks/list')}>
                Back
              </button>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Task Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default TaskForm

